import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { resolveRouteSeoRow } from '../seo/routeSeo';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { SITE_LOGO_URL, SITE_NAME, SITE_ORIGIN } from '../seo/site';

/**
 * Sets title, meta description, canonical, and Open Graph tags per route.
 * Quiz pages layer extra Helmet tags in QuizPlayer when quiz metadata loads.
 * Titles and descriptions follow the active UI language (en / pt-BR).
 */
const DocumentMeta: React.FC = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useSafeTranslation();
  const row = useMemo(() => resolveRouteSeoRow(pathname), [pathname]);
  const title = useMemo(
    () => t(row.titleKey, row.titleFb),
    [row.titleKey, row.titleFb, t, i18n.language]
  );
  const description = useMemo(
    () => t(row.descKey, row.descFb),
    [row.descKey, row.descFb, t, i18n.language]
  );
  const pathClean = pathname.replace(/\/$/, '') || '/';
  const canonical = pathClean === '/' ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${pathClean}`;
  const ogUrl = canonical;
  const isPt = (i18n.language || '').toLowerCase().startsWith('pt');
  const htmlLang = isPt ? 'pt-BR' : 'en';
  const ogLocale = isPt ? 'pt_BR' : 'en_US';

  return (
    <Helmet htmlAttributes={{ lang: htmlLang }}>
      <title>{title}</title>
      <meta name="description" content={description.slice(0, 320)} />
      <link rel="canonical" href={pathname === '/' ? SITE_ORIGIN + '/' : canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description.slice(0, 300)} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={SITE_LOGO_URL} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={isPt ? 'en_US' : 'pt_BR'} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description.slice(0, 200)} />
      <meta name="twitter:image" content={SITE_LOGO_URL} />
    </Helmet>
  );
};

export default DocumentMeta;
